import React, { useEffect, useState, useCallback } from 'react';
import api, { formatApiError } from '../../api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
    UserPlus, KeyRound, Pencil, Trash2, Copy, ShieldCheck, Send, Lock,
} from 'lucide-react';
import { MODULES, ACTIONS, ROLES } from '../../lib/permissions';
import { useAuth } from '../../contexts/AuthContext';

export default function UsersSettings() {
    const { user: currentUser, can, refreshUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [meta, setMeta] = useState(null);
    const [editing, setEditing] = useState(null);     // user being edited or 'new'
    const [resetting, setResetting] = useState(null); // user whose password we're resetting
    const [changingOwn, setChangingOwn] = useState(false);
    const canCreate = can('settings', 'create');
    const canEdit = can('settings', 'edit');
    const canDelete = can('settings', 'delete');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [u, m] = await Promise.all([api.get('/users'), api.get('/users/meta')]);
            setUsers(u.data); setMeta(m.data);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to load users');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const onDelete = async (u) => {
        if (!window.confirm(`Delete ${u.name} (${u.email})? This cannot be undone.`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            toast.success('User deleted');
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Delete failed');
        }
    };

    return (
        <div className="max-w-5xl">
            <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6 mb-5">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                    <div>
                        <div className="font-display text-[15px] font-semibold text-[color:var(--bm-text)]">Team members</div>
                        <div className="text-[12px] text-[color:var(--bm-text-muted)] mt-1">
                            Add users, assign role presets, fine-tune per-module permissions, and reset passwords.
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setChangingOwn(true)} className="h-9" data-testid="change-own-password-btn">
                            <Lock size={14} className="mr-1.5" /> Change my password
                        </Button>
                        {canCreate && (
                            <Button onClick={() => setEditing('new')} className="h-9"
                                style={{ background: 'var(--bm-primary)' }} data-testid="add-user-btn">
                                <UserPlus size={14} className="mr-1.5" /> Add user
                            </Button>
                        )}
                    </div>
                </div>

                {loading && <div className="text-[12.5px] text-[color:var(--bm-text-muted)]">Loading…</div>}

                {!loading && (
                    <div className="overflow-x-auto -mx-6">
                        <table className="w-full text-[13px]" data-testid="users-table">
                            <thead className="border-b border-[color:var(--bm-border)] bg-[color:var(--bm-bg-mute)]/50">
                                <tr className="text-left text-[11.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)]">
                                    <th className="py-2.5 px-6 font-semibold">Name</th>
                                    <th className="py-2.5 px-3 font-semibold">Email</th>
                                    <th className="py-2.5 px-3 font-semibold">Role</th>
                                    <th className="py-2.5 px-3 font-semibold">Status</th>
                                    <th className="py-2.5 px-3 font-semibold">Last login</th>
                                    <th className="py-2.5 px-3 font-semibold w-32 text-right pr-6">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(u => {
                                    const isSelf = currentUser?.id === u.id;
                                    return (
                                        <tr key={u.id} className="border-b border-[color:var(--bm-border)]/60 hover:bg-[color:var(--bm-bg-mute)]/30" data-testid={`user-row-${u.id}`}>
                                            <td className="py-3 px-6">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold text-white" style={{ background: 'var(--bm-primary)' }}>
                                                        {(u.name || 'U').charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div className="font-medium">{u.name}</div>
                                                        {u.is_super_admin && (
                                                            <div className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                                                                <ShieldCheck size={9} /> Super admin
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-3 px-3 text-[color:var(--bm-text-muted)]">{u.email}</td>
                                            <td className="py-3 px-3 capitalize">{u.role}</td>
                                            <td className="py-3 px-3">
                                                {u.active
                                                    ? u.password_set
                                                        ? <span className="text-emerald-700 font-medium">Active</span>
                                                        : <span className="text-amber-700 font-medium">Invite pending</span>
                                                    : <span className="text-[color:var(--bm-text-muted)]">Disabled</span>}
                                            </td>
                                            <td className="py-3 px-3 text-[color:var(--bm-text-muted)]">
                                                {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                            </td>
                                            <td className="py-3 px-3 text-right pr-6">
                                                <div className="inline-flex gap-1">
                                                    {canEdit && (
                                                        <IconBtn icon={Pencil} label="Edit" onClick={() => setEditing(u)} testId={`edit-user-${u.id}`} />
                                                    )}
                                                    {canEdit && (
                                                        <IconBtn icon={KeyRound} label="Reset password" onClick={() => setResetting(u)} testId={`reset-user-${u.id}`} />
                                                    )}
                                                    {canDelete && !u.is_super_admin && !isSelf && (
                                                        <IconBtn icon={Trash2} label="Delete" danger onClick={() => onDelete(u)} testId={`delete-user-${u.id}`} />
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {users.length === 0 && (
                                    <tr><td colSpan={6} className="py-8 text-center text-[color:var(--bm-text-muted)]">No users yet.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {editing && meta && (
                <UserDialog
                    user={editing === 'new' ? null : editing}
                    meta={meta}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); if (currentUser && editing?.id === currentUser.id) refreshUser?.(); }}
                />
            )}
            {resetting && (
                <ResetPasswordDialog
                    user={resetting}
                    onClose={() => setResetting(null)}
                    onDone={() => { setResetting(null); load(); }}
                />
            )}
            {changingOwn && (
                <ChangeOwnPasswordDialog onClose={() => setChangingOwn(false)} />
            )}
        </div>
    );
}

function IconBtn({ icon: Icon, label, onClick, danger, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={label}
            data-testid={testId}
            className={`h-8 w-8 inline-flex items-center justify-center rounded-md border border-transparent hover:bg-[color:var(--bm-bg-mute)] ${danger ? 'text-red-600 hover:bg-red-50' : 'text-[color:var(--bm-text-muted)]'}`}
        >
            <Icon size={14} />
        </button>
    );
}

function UserDialog({ user, meta, onClose, onSaved }) {
    const isEdit = !!user;
    const [form, setForm] = useState({
        name: user?.name || '',
        email: user?.email || '',
        role: user?.role || 'viewer',
        permissions: user?.permissions || {},
        active: user?.active ?? true,
        password: '',
        send_invite: true,
    });
    const [saving, setSaving] = useState(false);
    const [inviteResult, setInviteResult] = useState(null);

    const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const presetActsForRole = meta.role_presets?.[form.role] || {};

    const toggleAct = (module, action) => {
        const current = form.permissions?.[module];
        // If no override yet, start from the role preset acts
        const base = current ?? [...(presetActsForRole[module] || [])];
        const next = base.includes(action) ? base.filter(a => a !== action) : [...base, action];
        update('permissions', { ...form.permissions, [module]: next });
    };

    const clearOverride = (module) => {
        const { [module]: _drop, ...rest } = form.permissions || {};
        update('permissions', rest);
    };

    const effectiveActs = (module) => {
        const override = form.permissions?.[module];
        return override ?? (presetActsForRole[module] || []);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error('Name and email are required.');
            return;
        }
        if (!isEdit && !form.send_invite && (!form.password || form.password.length < 8)) {
            toast.error('Password must be at least 8 characters.');
            return;
        }
        setSaving(true);
        try {
            if (isEdit) {
                await api.patch(`/users/${user.id}`, {
                    name: form.name,
                    email: form.email,
                    role: form.role,
                    permissions: form.permissions,
                    active: form.active,
                });
                toast.success('User updated');
                onSaved();
            } else {
                const payload = {
                    name: form.name,
                    email: form.email,
                    role: form.role,
                    permissions: form.permissions,
                    active: form.active,
                };
                if (!form.send_invite) payload.password = form.password;
                const r = await api.post('/users', payload);
                if (r.data.invite_token) {
                    setInviteResult(r.data);
                    toast.success('User created — share the invite link with them.');
                    return;
                }
                toast.success('User created');
                onSaved();
            }
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || 'Save failed');
        } finally { setSaving(false); }
    };

    if (inviteResult) {
        const inviteUrl = `${window.location.origin}/accept-invite?token=${inviteResult.invite_token}`;
        return (
            <Dialog open onOpenChange={onClose}>
                <DialogContent className="max-w-lg" data-testid="invite-link-dialog">
                    <DialogHeader>
                        <DialogTitle>Invite link for {inviteResult.name}</DialogTitle>
                        <DialogDescription>
                            Send this link to {inviteResult.email}. It expires in 24 hours.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="bg-[color:var(--bm-bg-mute)] border border-[color:var(--bm-border)] rounded px-3 py-2 text-[12.5px] break-all font-mono" data-testid="invite-link-value">
                        {inviteUrl}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { navigator.clipboard.writeText(inviteUrl); toast.success('Copied'); }} data-testid="copy-invite-link-btn">
                            <Copy size={13} className="mr-1.5" /> Copy link
                        </Button>
                        <Button onClick={() => { setInviteResult(null); onSaved(); }} style={{ background: 'var(--bm-primary)' }} data-testid="invite-done-btn">Done</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="user-dialog">
                <DialogHeader>
                    <DialogTitle>{isEdit ? `Edit ${user.name}` : 'Add user'}</DialogTitle>
                    <DialogDescription>
                        {isEdit ? 'Update name, role and per-module permissions.' : 'Create a new user. They can be invited by email link or set up with a temporary password.'}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={onSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[12px] font-medium">Full name</Label>
                            <Input value={form.name} onChange={e => update('name', e.target.value)} className="mt-1 h-9" data-testid="user-name-input" />
                        </div>
                        <div>
                            <Label className="text-[12px] font-medium">Email</Label>
                            <Input type="email" value={form.email} onChange={e => update('email', e.target.value)} className="mt-1 h-9" data-testid="user-email-input" disabled={user?.is_super_admin} />
                        </div>
                        <div>
                            <Label className="text-[12px] font-medium">Role</Label>
                            <select
                                value={form.role}
                                onChange={e => { update('role', e.target.value); update('permissions', {}); }}
                                className="mt-1 h-9 w-full rounded-md border border-[color:var(--bm-border)] bg-white px-3 text-[13px]"
                                disabled={user?.is_super_admin}
                                data-testid="user-role-select"
                            >
                                {ROLES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                            </select>
                            <div className="text-[11px] text-[color:var(--bm-text-subtle)] mt-1">
                                {ROLES.find(r => r.key === form.role)?.help}
                            </div>
                        </div>
                        <div>
                            <Label className="text-[12px] font-medium">Status</Label>
                            <label className="mt-1 flex items-center gap-2 h-9 px-3 rounded-md border border-[color:var(--bm-border)] bg-white text-[13px]">
                                <input type="checkbox" checked={form.active} onChange={e => update('active', e.target.checked)} disabled={user?.is_super_admin} />
                                Active (can sign in)
                            </label>
                        </div>
                    </div>

                    {!isEdit && (
                        <div className="bg-[color:var(--bm-bg-mute)] rounded-md p-3 border border-[color:var(--bm-border)]">
                            <div className="text-[12px] font-medium mb-2">How should they get access?</div>
                            <label className="flex items-start gap-2 text-[12.5px] cursor-pointer">
                                <input type="radio" checked={form.send_invite} onChange={() => update('send_invite', true)} className="mt-1" />
                                <span>
                                    <span className="font-medium">Email invite link</span> — generate a one-time link they can use to set their own password.
                                </span>
                            </label>
                            <label className="flex items-start gap-2 text-[12.5px] cursor-pointer mt-2">
                                <input type="radio" checked={!form.send_invite} onChange={() => update('send_invite', false)} className="mt-1" />
                                <span className="flex-1">
                                    <span className="font-medium">Set a temporary password</span> — they sign in with this and can change it after.
                                    {!form.send_invite && (
                                        <Input type="password" placeholder="Min 8 characters" value={form.password}
                                            onChange={e => update('password', e.target.value)}
                                            className="mt-2 h-9" data-testid="user-temp-password-input" />
                                    )}
                                </span>
                            </label>
                        </div>
                    )}

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <Label className="text-[12px] font-medium">Permissions</Label>
                            <div className="text-[11px] text-[color:var(--bm-text-subtle)]">
                                Defaults from <span className="capitalize font-medium">{form.role}</span> role. Tick to override.
                            </div>
                        </div>
                        <div className="border border-[color:var(--bm-border)] rounded-md overflow-hidden" data-testid="permissions-grid">
                            <table className="w-full text-[12.5px]">
                                <thead className="bg-[color:var(--bm-bg-mute)] border-b border-[color:var(--bm-border)]">
                                    <tr>
                                        <th className="text-left py-2 px-3 font-semibold">Module</th>
                                        {ACTIONS.map(a => <th key={a.key} className="py-2 px-2 font-semibold text-center w-20">{a.label}</th>)}
                                        <th className="py-2 px-2 font-semibold text-right pr-3 w-24"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {MODULES.map(m => {
                                        const acts = effectiveActs(m.key);
                                        const isOverride = Object.prototype.hasOwnProperty.call(form.permissions || {}, m.key);
                                        return (
                                            <tr key={m.key} className="border-b border-[color:var(--bm-border)]/60 last:border-0">
                                                <td className="py-2 px-3">
                                                    <div className="font-medium">{m.label}</div>
                                                    {isOverride && (
                                                        <div className="text-[10px] text-amber-700 font-medium">Custom override</div>
                                                    )}
                                                </td>
                                                {ACTIONS.map(a => (
                                                    <td key={a.key} className="text-center py-2 px-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={acts.includes(a.key)}
                                                            disabled={user?.is_super_admin}
                                                            onChange={() => toggleAct(m.key, a.key)}
                                                            data-testid={`perm-${m.key}-${a.key}`}
                                                        />
                                                    </td>
                                                ))}
                                                <td className="text-right pr-3">
                                                    {isOverride && (
                                                        <button type="button" onClick={() => clearOverride(m.key)} className="text-[11px] text-[color:var(--bm-text-muted)] hover:text-[color:var(--bm-text)] underline">
                                                            Reset
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {user?.is_super_admin && (
                            <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                                Super admin has full access to every module and cannot be modified here.
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" disabled={saving} style={{ background: 'var(--bm-primary)' }} data-testid="user-save-btn">
                            <Send size={13} className="mr-1.5" /> {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create user'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function ResetPasswordDialog({ user, onClose, onDone }) {
    const [newPassword, setNewPassword] = useState('');
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (newPassword.length < 8) return toast.error('Password must be at least 8 characters.');
        setSaving(true);
        try {
            await api.post(`/users/${user.id}/reset-password`, { new_password: newPassword });
            toast.success(`Password reset for ${user.name}`);
            onDone();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || 'Reset failed');
        } finally { setSaving(false); }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-md" data-testid="reset-password-dialog">
                <DialogHeader>
                    <DialogTitle>Reset password — {user.name}</DialogTitle>
                    <DialogDescription>Set a new password for this user. They can change it after signing in.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <div>
                        <Label className="text-[12px] font-medium">New password</Label>
                        <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                            className="mt-1 h-9" placeholder="Min 8 characters" autoFocus data-testid="reset-password-input" />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" disabled={saving} style={{ background: 'var(--bm-primary)' }} data-testid="reset-password-submit">
                            {saving ? 'Resetting…' : 'Reset password'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function ChangeOwnPasswordDialog({ onClose }) {
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (next.length < 8) return toast.error('New password must be at least 8 characters.');
        if (next !== confirm) return toast.error('Passwords do not match.');
        setSaving(true);
        try {
            await api.post('/users/me/change-password', { current_password: current, new_password: next });
            toast.success('Password changed successfully');
            onClose();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || 'Change failed');
        } finally { setSaving(false); }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-md" data-testid="change-own-password-dialog">
                <DialogHeader>
                    <DialogTitle>Change my password</DialogTitle>
                    <DialogDescription>Enter your current password and a new one to update your sign-in credentials.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <div>
                        <Label className="text-[12px] font-medium">Current password</Label>
                        <Input type="password" value={current} onChange={e => setCurrent(e.target.value)} className="mt-1 h-9" autoFocus data-testid="current-password-input" />
                    </div>
                    <div>
                        <Label className="text-[12px] font-medium">New password</Label>
                        <Input type="password" value={next} onChange={e => setNext(e.target.value)} className="mt-1 h-9" placeholder="Min 8 characters" data-testid="new-password-input" />
                    </div>
                    <div>
                        <Label className="text-[12px] font-medium">Confirm new password</Label>
                        <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} className="mt-1 h-9" data-testid="confirm-password-input" />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" disabled={saving} style={{ background: 'var(--bm-primary)' }} data-testid="change-own-password-submit">
                            {saving ? 'Saving…' : 'Change password'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
